// Centralized User Session Registry (single source of truth for user data and auth)
// Responsibilities:
// - Provide current user data with backward compatibility (overloaded getCurrentUser)
// - Build auth headers centrally (X-User-Email, X-User-Role, X-User-Org, Authorization)
// - Get user's assigned agents (uses AgentsRegistry internally)
// - Cache user data with TTL
// - Deduplicate concurrent requests

import { useEffect, useState } from 'react';
import { AgentsRegistry } from './agents.registry';
import { apiCall } from '../services/api.service';
import { auth as firebaseAuth } from '../config/firebase';

// Types matching AuthContext for backward compatibility
type FirebaseUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
  [key: string]: any;
};

type UserInfo = {
  user: {
    id: number;
    uid: string;
    email: string;
    name: string | null;
    firebaseUid: string;
    role?: string;
    members?: any[];
    modules?: any[];
    bt_id?: number;
    extra_data?: any;
    [key: string]: any;
  };
};

type CurrentUserData = {
  firebaseUser: FirebaseUser | null;
  userInfo: UserInfo | null;
  role: string | null;
  organizationId: number | null;
  email: string | null;
  fetchedAt: number;
  expiresAt: number;
};

// TTLs
const USER_DATA_TTL_MS = 2 * 60 * 1000; // 2 minutes
const AUTH_HEADERS_TTL_MS = 5 * 60 * 1000; // 5 minutes (reduced rebuilds, token refresh handled separately)
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000; // Refresh token 5 minutes before expiry

function isTokenExpiringSoon(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = (payload.exp || 0) * 1000;
    return expiresAt - Date.now() < TOKEN_REFRESH_MARGIN_MS;
  } catch {
    return true;
  }
}

// Firebase auth-state hydration gate (Safari/ITP).
// Firebase restores its session from IndexedDB ASYNCHRONOUSLY; on slow or
// hostile storage (Safari ITP, private windows) getAuth().currentUser is still
// null when the first apiCalls fire, so requests go out WITHOUT a Bearer and
// ride only on the CLU session key. The backend accepts that (session keys are
// first-class identities), but the Bearer should accompany requests whenever a
// Firebase session actually exists. This gate waits ONCE for the first
// onAuthStateChanged emission (auth state settled) with a hard timeout so the
// app never blocks when there is no session at all; every later call resolves
// instantly.
let _authReadyPromise: Promise<void> | null = null;

function waitForFirebaseAuthReady(timeoutMs = 4000): Promise<void> {
  if (_authReadyPromise) return _authReadyPromise;
  _authReadyPromise = new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };
    const timer = setTimeout(finish, timeoutMs);
    (async () => {
      try {
        const { onAuthStateChanged } = await import('firebase/auth');
        const unsubscribe = onAuthStateChanged(firebaseAuth, () => {
          try { unsubscribe(); } catch { /* noop */ }
          clearTimeout(timer);
          finish();
        });
      } catch {
        clearTimeout(timer);
        finish();
      }
    })();
  });
  return _authReadyPromise;
}

// Internal store state
const state = {
  currentUserData: null as CurrentUserData | null,
  authHeadersCache: new Map<string, { headers: Record<string, string>; expiresAt: number }>(),
  inFlight: {
    userData: null as Promise<CurrentUserData> | null,
    headers: null as Promise<Record<string, string>> | null,
  },
  subscribers: new Set<() => void>(),
  loading: {
    userData: false,
    headers: false,
  },
  error: {
    userData: null as string | null,
    headers: null as string | null,
  },
  cluSessionKey: null as string | null,
  cluSessionKeyExpiresAt: 0,
  sessionExchangeInFlight: null as Promise<void> | null,
  lastExchangeCompletedAt: 0,
  currentFirebaseUid: null as string | null,
};

// Session key restore is deferred to syncFromAuthContext when Firebase UID is known.
// Restoring here without UID would allow cross-user key reuse (Zero Trust violation).

function notify() {
  state.subscribers.forEach((cb) => {
    try { cb(); } catch {}
  });
}

function now() {
  return Date.now();
}

function isUserDataFresh(): boolean {
  if (!state.currentUserData) return false;
  return state.currentUserData.expiresAt > now();
}

function _sessionKey(uid: string) { return `clu_session_${uid}`; }
function _sessionExpiry(uid: string) { return `clu_session_${uid}_exp`; }

// Cross-tab session sync: when another tab completes an exchange, pick up the new key
// so this tab doesn't trigger a parallel exchange that would deactivate it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (!state.currentFirebaseUid) return;
    if (e.key === _sessionKey(state.currentFirebaseUid) && e.newValue) {
      state.cluSessionKey = e.newValue;
      try {
        const exp = localStorage.getItem(_sessionExpiry(state.currentFirebaseUid));
        if (exp) state.cluSessionKeyExpiresAt = Number(exp);
      } catch {}
      state.authHeadersCache.clear();
      notify();
    }
  });
}

function isHeadersFresh(cacheKey: string): boolean {
  const cached = state.authHeadersCache.get(cacheKey);
  if (!cached) return false;
  return cached.expiresAt > now();
}

/**
 * Load user data from multiple sources (localStorage, AuthContext if available)
 * This provides backward compatibility with existing code
 */
async function loadUserData(): Promise<CurrentUserData> {
  console.log('[UserSessionRegistry.loadUserData] 🔍 Iniciando carga de datos de usuario...');
  
  // Try to get from localStorage first (fastest, most common)
  try {
    const authDataStr = localStorage.getItem('authData');
    const userStr = localStorage.getItem('user');
    const activeOrgStr = localStorage.getItem('activeOrganization');
    
    console.log('[UserSessionRegistry.loadUserData] 📋 localStorage check:', {
      hasAuthData: !!authDataStr,
      hasUser: !!userStr,
      hasActiveOrg: !!activeOrgStr,
    });
    
    if (authDataStr && userStr) {
      const authData = JSON.parse(authDataStr);
      const user = JSON.parse(userStr);
      const activeOrg = activeOrgStr ? JSON.parse(activeOrgStr) : null;
      
      console.log('[UserSessionRegistry.loadUserData] 📋 Datos parseados:', {
        'authData.role_code': authData.role_code || '[MISSING]',
        'authData.email': authData.email || '[MISSING]',
        'authData.organization_id': authData.organization_id || '[MISSING]',
        'user.email': user.email || '[MISSING]',
        'user.role': user.role || '[MISSING]',
        'activeOrg?.organization_id': activeOrg?.organization_id || '[MISSING]',
      });
      
      // Build FirebaseUser-like object from stored data
      const firebaseUser: FirebaseUser = {
        uid: user.firebaseUid || authData.firebase_uid || '',
        email: user.email || authData.email || null,
        displayName: user.name || null,
      };
      
      // Build UserInfo-like object
      const userInfo: UserInfo = {
        user: {
          id: user.id || 0,
          uid: user.uid || '',
          email: user.email || authData.email || '',
          name: user.name || null,
          firebaseUid: user.firebaseUid || authData.firebase_uid || '',
          role: authData.role_code || user.role || null,
          members: user.members || [],
          modules: user.modules || [],
          bt_id: user.bt_id || 0,
          extra_data: user.extra_data || authData.extra_data || {},
          ...user,
        },
      };
      
      const role = authData.role_code || user.role || null;
      const orgId = activeOrg?.organization_id || authData.organization_id || null;
      const email = user.email || authData.email || null;
      
      const result = {
        firebaseUser,
        userInfo,
        role,
        organizationId: orgId ? Number(orgId) : null,
        email,
        fetchedAt: now(),
        expiresAt: now() + USER_DATA_TTL_MS,
      };
      
      console.log('[UserSessionRegistry.loadUserData] ✅ Datos construidos:', {
        email: result.email || '[MISSING]',
        role: result.role || '[MISSING]',
        organizationId: result.organizationId || '[MISSING]',
      });
      
      return result;
    } else {
      console.warn('[UserSessionRegistry.loadUserData] ⚠️ No hay authData o user en localStorage');
    }
  } catch (error) {
    console.error('[UserSessionRegistry.loadUserData] ❌ Error loading from localStorage:', error);
    console.error('[UserSessionRegistry.loadUserData] Error details:', error.message, error.stack);
  }
  
  // Fallback: try to fetch from backend if available
  // BUT: Only if we have a Firebase user AND a valid token (don't call if not authenticated)
  try {
    // Check if Firebase auth has a current user before making API call
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    if (!auth.currentUser) {
      console.log('[UserSessionRegistry.loadUserData] No Firebase user, skipping auth/user-info call');
      throw new Error('No Firebase user available');
    }
    
    // Guard 1: Prevent deadlock — if getAuthHeaders is already building, skip this call.
    // loadUserData → apiCall → getAuthHeaders (in-flight) → getCurrentUser → loadUserData = circular wait.
    if (state.inFlight.headers) {
      console.log('[UserSessionRegistry.loadUserData] Skipping auth/user-info call — getAuthHeaders in-flight (deadlock prevention)');
      throw new Error('getAuthHeaders in-flight');
    }

    // Guard 2: Verify we have a valid, non-expired token before calling the endpoint.
    try {
      const authDataStr = localStorage.getItem('authData');
      if (!authDataStr) {
        console.log('[UserSessionRegistry.loadUserData] No authData in localStorage, skipping auth/user-info call');
        throw new Error('No authData available');
      }

      const authData = JSON.parse(authDataStr);
      if (!authData.token) {
        console.log('[UserSessionRegistry.loadUserData] No token in authData, skipping auth/user-info call');
        throw new Error('No token available');
      }

      if (isTokenExpiringSoon(authData.token)) {
        console.log('[UserSessionRegistry.loadUserData] Token expired/expiring, skipping auth/user-info call');
        throw new Error('Token expired');
      }

      console.log('[UserSessionRegistry.loadUserData] Token valid, calling auth/user-info endpoint');
    } catch (tokenCheckError) {
      console.log('[UserSessionRegistry.loadUserData] Token check failed, skipping auth/user-info call:', tokenCheckError.message);
      throw new Error('No valid token available');
    }
    
    // Call the endpoint - it requires authentication (token or API key)
    const userInfoResponse = await apiCall('auth/user-info');
    if (userInfoResponse) {
      const firebaseUser: FirebaseUser = {
        uid: userInfoResponse.firebase_uid || '',
        email: userInfoResponse.email || null,
        displayName: userInfoResponse.name || null,
      };
      
      const userInfo: UserInfo = {
        user: {
          id: userInfoResponse.id || 0,
          uid: userInfoResponse.uid || '',
          email: userInfoResponse.email || '',
          name: userInfoResponse.name || null,
          firebaseUid: userInfoResponse.firebase_uid || '',
          role: userInfoResponse.role_code || null,
          members: userInfoResponse.members || [],
          modules: userInfoResponse.modules || [],
          bt_id: userInfoResponse.bt_id || 0,
          extra_data: userInfoResponse.extra_data || {},
          ...userInfoResponse,
        },
      };
      
      return {
        firebaseUser,
        userInfo,
        role: userInfoResponse.role_code || null,
        organizationId: userInfoResponse.organization_id || null,
        email: userInfoResponse.email || null,
        fetchedAt: now(),
        expiresAt: now() + USER_DATA_TTL_MS,
      };
    }
  } catch (error) {
    // Handle 401 gracefully - it's expected when there's no valid token
    // Don't log as error, just as info since we have localStorage fallback
    if (error?.message?.includes('401') || error?.response?.status === 401) {
      console.log('[UserSessionRegistry.loadUserData] 401 Unauthorized - no valid token, using localStorage data');
    } else {
      console.warn('[UserSessionRegistry.loadUserData] Error fetching from backend:', error.message || error);
    }
  }
  
  // Return empty data if nothing found
  return {
    firebaseUser: null,
    userInfo: null,
    role: null,
    organizationId: null,
    email: null,
    fetchedAt: now(),
    expiresAt: now() + USER_DATA_TTL_MS,
  };
}

async function _doSessionExchange(firebaseToken: string, isRetry = false): Promise<void> {
  if (!firebaseToken) {
    console.warn('[UserSessionRegistry._doSessionExchange] No token provided, skipping exchange');
    return;
  }
  try {
    state.lastExchangeCompletedAt = now(); // trip cooldown guard even on abort/failure
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10_000);
    let res: Response;
    try {
      res = await fetch(`${apiUrl}/api/v1/session/exchange`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Accept': 'application/json',
        },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
    if (!res.ok) {
      if (res.status === 401 && !isRetry) {
        try {
          const { getAuth } = await import('firebase/auth');
          const freshToken = await getAuth().currentUser?.getIdToken(true);
          if (freshToken && freshToken !== firebaseToken) {
            state.authHeadersCache.clear();
            return _doSessionExchange(freshToken, true);
          }
        } catch (refreshErr) {
          console.warn('[UserSessionRegistry._doSessionExchange] Force refresh failed:', refreshErr);
        }
      }
      console.warn('[UserSessionRegistry._doSessionExchange] Exchange failed:', res.status);
      return;
    }
    const data = await res.json();
    if (data.key_value) {
      const expiresAt = new Date(data.expires_at).getTime();
      state.cluSessionKey = data.key_value;
      state.cluSessionKeyExpiresAt = expiresAt;
      state.lastExchangeCompletedAt = now();
      const _uid = state.currentFirebaseUid;
      if (_uid) {
        localStorage.setItem(_sessionKey(_uid), data.key_value);
        localStorage.setItem(_sessionExpiry(_uid), String(expiresAt));
      }
      try { localStorage.removeItem('clu_session_key'); localStorage.removeItem('clu_session_key_expires_at'); } catch {}
      state.authHeadersCache.clear();
      console.log('[UserSessionRegistry._doSessionExchange] ✅ CLU session key obtained');
      notify();
    }
  } catch (err) {
    console.warn('[UserSessionRegistry._doSessionExchange] Error during exchange:', err);
  }
}

// THE single place allowed to claim state.sessionExchangeInFlight and call
// _doSessionExchange. 2026-10-01 incident: getAuthHeaders, syncFromAuthContext
// and recoverSession each had their OWN "if (!state.sessionExchangeInFlight)
// { ...claim...; _doSessionExchange(...) }" — each individually safe against
// itself (no await between its own check and its own claim), but three
// independent copies of the same decision still raced each other: while one
// site was awaiting something ELSE before reaching its check (resolving a
// Firebase token, waiting for onAuthStateChanged), another site could run,
// claim the slot, complete an exchange, and have that exchange's
// deactivate_old_session_keys() kill a key a third site was about to use —
// producing the observed 403s/429s even though no single site's own guard
// was individually broken. Centralizing the claim here doesn't change when
// each caller DECIDES to request a key (that policy stays at the call
// site — cooldown, destructive recovery, cold-start) — it only guarantees
// the actual claim-and-exchange is the same synchronous critical section
// everywhere, so there is exactly one place to reason about instead of three.
//
// This function itself must stay synchronous up to the claim (no `await`
// before `state.sessionExchangeInFlight = ...`) — that's what makes the
// claim atomic. The IIFE that performs the actual token-resolve + exchange
// runs AFTER the claim, not before it.
//
// Exported ONLY for tests (see __tests__/user-session.registry.exchange.test.ts)
// — every other call site in this file must go through the three public
// methods (getAuthHeaders / syncFromAuthContext / recoverSession), never
// this function directly, or the "single gate" property this fixes stops
// being true.
export function _claimOrJoinSessionExchange(
  getToken: () => Promise<string | null | undefined>,
): { started: boolean; promise: Promise<void> } {
  if (state.sessionExchangeInFlight) {
    return { started: false, promise: state.sessionExchangeInFlight };
  }

  const exchangePromise = (async () => {
    const token = await getToken();
    if (!token) return;
    await _doSessionExchange(token);
  })();

  state.sessionExchangeInFlight = exchangePromise.finally(() => {
    state.sessionExchangeInFlight = null;
  });

  return { started: true, promise: state.sessionExchangeInFlight };
}

export const UserSessionRegistry = {
  /**
   * Get current user data (overloaded for backward compatibility)
   * 
   * Usage:
   * - getCurrentUser() → returns full CurrentUserData object
   * - getCurrentUser('email') → returns email string
   * - getCurrentUser('role') → returns role string
   * - getCurrentUser('organizationId') → returns organization ID number
   * - getCurrentUser('firebaseUser') → returns FirebaseUser object
   * - getCurrentUser('userInfo') → returns UserInfo object
   */
  async getCurrentUser(property?: string): Promise<any> {
    // PRIORITY 1: Return cached synchronized data if available (even if expired)
    // This ensures we use data synced from AuthContext (single source of truth)
    if (state.currentUserData) {
      // Check if data is fresh OR if it was synced from AuthContext (has fetchedAt)
      // Synced data from AuthContext should always be used, even if TTL expired
      if (isUserDataFresh() || state.currentUserData.fetchedAt > 0) {
        if (!property) {
          return state.currentUserData;
        }
        
        // Return specific property
        switch (property) {
          case 'email':
            return state.currentUserData.email;
          case 'role':
            return state.currentUserData.role;
          case 'organizationId':
          case 'orgId':
            return state.currentUserData.organizationId;
          case 'firebaseUser':
          case 'currentUser':
            return state.currentUserData.firebaseUser;
          case 'userInfo':
            return state.currentUserData.userInfo;
          default:
            // Try to get from userInfo.user[property]
            return state.currentUserData.userInfo?.user?.[property] || null;
        }
      }
    }
    
    // Deduplicate concurrent requests
    if (state.inFlight.userData) {
      const data = await state.inFlight.userData;
      if (!property) return data;
      
      switch (property) {
        case 'email': return data.email;
        case 'role': return data.role;
        case 'organizationId':
        case 'orgId': return data.organizationId;
        case 'firebaseUser':
        case 'currentUser': return data.firebaseUser;
        case 'userInfo': return data.userInfo;
        default: return data.userInfo?.user?.[property] || null;
      }
    }
    
    // PRIORITY 2: Load from localStorage only if no synced data exists
    // This is a fallback for page reloads before AuthContext syncs
    state.loading.userData = true;
    state.error.userData = null;
    const promise = loadUserData().then((data) => {
      // Only update if we don't have synced data, or if loaded data is fresher
      if (!state.currentUserData || data.fetchedAt > state.currentUserData.fetchedAt) {
        state.currentUserData = data;
      }
      state.inFlight.userData = null;
      state.loading.userData = false;
      notify();
      
      if (!property) return state.currentUserData || data;
      
      const finalData = state.currentUserData || data;
      switch (property) {
        case 'email': return finalData.email;
        case 'role': return finalData.role;
        case 'organizationId':
        case 'orgId': return finalData.organizationId;
        case 'firebaseUser':
        case 'currentUser': return finalData.firebaseUser;
        case 'userInfo': return finalData.userInfo;
        default: return finalData.userInfo?.user?.[property] || null;
      }
    }).catch((error) => {
      state.error.userData = error.message || 'Failed to load user data';
      state.inFlight.userData = null;
      state.loading.userData = false;
      notify();
      throw error;
    });
    
    state.inFlight.userData = promise;
    return promise;
  },
  
  /**
   * Convenience methods (backward compatibility)
   */
  async getCurrentUserEmail(): Promise<string | null> {
    return this.getCurrentUser('email');
  },
  
  async getCurrentUserRole(): Promise<string | null> {
    return this.getCurrentUser('role');
  },
  
  async getCurrentOrganizationId(): Promise<number | null> {
    return this.getCurrentUser('organizationId');
  },
  
  /**
   * Get auth headers for API requests
   * @param options - { orgId?: number, includeToken?: boolean }
   */
  async getAuthHeaders(options: { orgId?: number; includeToken?: boolean } = {}): Promise<Record<string, string>> {
    const { orgId, includeToken = true } = options;
    const cacheKey = `${orgId || 'default'}|${includeToken}`;
    
    // Return cached if fresh
    if (isHeadersFresh(cacheKey)) {
      const cached = state.authHeadersCache.get(cacheKey);
      if (cached) {
        // Invalidate cache if the token inside is expiring soon
        const cachedAuth = cached.headers['Authorization'];
        if (cachedAuth) {
          const cachedToken = cachedAuth.replace('Bearer ', '');
          if (isTokenExpiringSoon(cachedToken)) {
            state.authHeadersCache.delete(cacheKey);
          } else {
            return cached.headers;
          }
        } else {
          return cached.headers;
        }
      }
    }
    
    // Deduplicate concurrent requests
    if (state.inFlight.headers) {
      return state.inFlight.headers;
    }
    
    // Start build
    state.loading.headers = true;
    state.error.headers = null;
    const promise = (async () => {
      try {
        console.log('[UserSessionRegistry.getAuthHeaders] 🔍 Obteniendo userData desde getCurrentUser()...');
        const userData = await this.getCurrentUser();
        console.log('[UserSessionRegistry.getAuthHeaders] 📋 userData obtenido:', {
          email: userData?.email || '[MISSING]',
          role: userData?.role || '[MISSING]',
          organizationId: userData?.organizationId || '[MISSING]',
          hasUserInfo: !!userData?.userInfo,
          hasFirebaseUser: !!userData?.firebaseUser,
        });
        
        const headers: Record<string, string> = {
          'Accept': 'application/json',
          // Note: Content-Type should be set by the caller based on request body type
          // We don't set it here to allow flexibility for FormData, etc.
        };
        
        // X-User-Email - Solo desde datos sincronizados (fuente de verdad única)
        if (userData?.email) {
          headers['X-User-Email'] = userData.email;
          console.log('[UserSessionRegistry.getAuthHeaders] ✅ X-User-Email:', userData.email);
        } else {
          console.error('[UserSessionRegistry.getAuthHeaders] ❌ No hay email en userData sincronizado. AuthContext debería haber sincronizado estos datos.');
          // NO usar fallback de localStorage - AuthContext es la fuente de verdad única
        }
        
        // X-User-Role - Solo desde datos sincronizados (fuente de verdad única)
        if (userData?.role) {
          headers['X-User-Role'] = userData.role;
          console.log('[UserSessionRegistry.getAuthHeaders] ✅ X-User-Role:', userData.role);
        } else {
          console.error('[UserSessionRegistry.getAuthHeaders] ❌ No hay role en userData sincronizado. AuthContext debería haber sincronizado estos datos.');
          // NO usar fallback de localStorage - AuthContext es la fuente de verdad única
        }
        
        // X-User-Org (priority: options.orgId > userData.organizationId)
        // Usar datos sincronizados como fuente de verdad única
        let effectiveOrgId = orgId;
        if (!effectiveOrgId) {
          effectiveOrgId = userData?.organizationId;
        }
        if (effectiveOrgId) {
          headers['X-User-Org'] = String(effectiveOrgId);
        } else {
          console.warn('[UserSessionRegistry.getAuthHeaders] ⚠️ No hay organizationId disponible');
        }
        
        // Authorization token
        // Strategy: get token from Firebase, force-refresh if expiring soon
        if (includeToken) {
          try {
            const authDataStr = localStorage.getItem('authData');
            const authData = authDataStr ? JSON.parse(authDataStr) : {};

            let resolvedToken: string | null = null;
            try {
              const { getAuth } = await import('firebase/auth');
              let firebaseUser = getAuth().currentUser;
              if (!firebaseUser) {
                // Safari/ITP: auth state may still be hydrating from IndexedDB —
                // wait for it to settle (bounded) before concluding there is no
                // Firebase session and dropping the Bearer for this request.
                await waitForFirebaseAuthReady();
                firebaseUser = getAuth().currentUser;
              }
              if (firebaseUser) {
                // First try cached token (fast path)
                resolvedToken = await firebaseUser.getIdToken(false);
                // If token is expiring soon, force a refresh to avoid 401
                if (resolvedToken && isTokenExpiringSoon(resolvedToken)) {
                  console.log('[UserSessionRegistry.getAuthHeaders] ⏰ Token expiring soon, forcing refresh...');
                  resolvedToken = await firebaseUser.getIdToken(true);
                  console.log('[UserSessionRegistry.getAuthHeaders] ✅ Token force-refreshed successfully');
                }
              }
            } catch (firebaseTokenError) {
              console.warn('[UserSessionRegistry.getAuthHeaders] ⚠️ Firebase token resolution failed, trying localStorage fallback:', firebaseTokenError);
            }

            if (!resolvedToken && authData?.token) {
              resolvedToken = authData.token;
              // Even for localStorage fallback, check if token is expired
              if (isTokenExpiringSoon(resolvedToken)) {
                console.warn('[UserSessionRegistry.getAuthHeaders] ⚠️ localStorage token is expired/expiring, clearing');
                resolvedToken = null;
              }
            }

            if (resolvedToken) {
              headers['Authorization'] = `Bearer ${resolvedToken}`;
              authData.token = resolvedToken;
              authData.last_updated = new Date().toISOString();
              localStorage.setItem('authData', JSON.stringify(authData));

              // Exchange Firebase token for CLU session key if not available or expired.
              // Only AWAIT if someone else's exchange is already running (join it).
              // On cold start (we're the one claiming it) we fire-and-forget so the
              // screen doesn't block for 1-2s. Claim/join goes through the single
              // shared gate (_claimOrJoinSessionExchange) — see its comment for why.
              if (!state.cluSessionKey || state.cluSessionKeyExpiresAt <= now()) {
                const { started, promise } = _claimOrJoinSessionExchange(async () => resolvedToken);
                if (!started) {
                  try { await promise; } catch {}
                }
              }
            } else {
              console.warn('[UserSessionRegistry.getAuthHeaders] ⚠️ No valid authorization token available');
            }
          } catch (error) {
            console.error('[UserSessionRegistry.getAuthHeaders] Error getting auth token:', error);
          }
        }

        // X-API-Key — CLU session key for API-key-protected endpoints
        if (state.cluSessionKey && state.cluSessionKeyExpiresAt > now()) {
          headers['X-API-Key'] = state.cluSessionKey;
        }

        // Prevent caching token-less headers for token-required flows.
        const hasAuthorization = !!headers['Authorization'];
        if (!includeToken || hasAuthorization) {
          state.authHeadersCache.set(cacheKey, {
            headers,
            expiresAt: now() + AUTH_HEADERS_TTL_MS,
          });
        } else {
          state.authHeadersCache.delete(cacheKey);
        }
        state.loading.headers = false;
        notify();
        return headers;
      } catch (error: any) {
        state.error.headers = error.message || 'Failed to build auth headers';
        state.loading.headers = false;
        notify();
        throw error;
      } finally {
        state.inFlight.headers = null;
      }
    })();
    
    state.inFlight.headers = promise;
    return promise;
  },
  
  /**
   * Get user's assigned agents (uses AgentsRegistry internally)
   */
  async getUserAgents(forceRefresh = false): Promise<any[]> {
    // Get current user data to pass to AgentsRegistry
    const userData = await this.getCurrentUser();
    if (!userData?.email) {
      console.warn('[UserSessionRegistry] getUserAgents called but no email available');
      return [];
    }
    // Use AgentsRegistry internally - it already handles caching and permissions
    return AgentsRegistry.loadAgents({
      email: userData.email,
      orgId: userData.organizationId,
      role: userData.role,
      force: forceRefresh,
    });
  },
  
  /**
   * Sync user data from AuthContext (called by AuthContext when state changes)
   * This makes AuthContext the single source of truth, and Registry just mirrors it
   */
  syncFromAuthContext(userData: {
    firebaseUser?: FirebaseUser | null;
    userInfo?: UserInfo | null;
    role?: string | null;
    organizationId?: number | null;
    email?: string | null;
  }) {
    console.log('[UserSessionRegistry.syncFromAuthContext] 🔄 Sincronizando datos desde AuthContext:', {
      hasFirebaseUser: !!userData.firebaseUser,
      hasUserInfo: !!userData.userInfo,
      role: userData.role || '[MISSING]',
      email: userData.email || '[MISSING]',
      organizationId: userData.organizationId || '[MISSING]',
    });
    
    // Always update if we have any data (even if some fields are null)
    // This ensures we clear stale data when user logs out
    if (userData.firebaseUser !== undefined || userData.userInfo !== undefined || userData.role !== undefined || userData.email !== undefined) {
      // Extract email and role from multiple sources for robustness
      const email = userData.email || 
                    userData.userInfo?.user?.email || 
                    userData.firebaseUser?.email || 
                    state.currentUserData?.email || 
                    null;
      
      const role = userData.role || 
                   userData.userInfo?.user?.role_code || 
                   userData.userInfo?.user?.role || 
                   state.currentUserData?.role || 
                   null;
      
      const organizationId = userData.organizationId ?? 
                             userData.userInfo?.user?.organization_id ?? 
                             state.currentUserData?.organizationId ?? 
                             null;
      
      console.log('[UserSessionRegistry.syncFromAuthContext] 📋 Datos extraídos:', {
        email: email || '[MISSING]',
        role: role || '[MISSING]',
        organizationId: organizationId || '[MISSING]',
      });

      // Zero Trust: detect UID change and restore/reset session accordingly
      const newUid = userData.firebaseUser?.uid || null;
      if (newUid !== state.currentFirebaseUid) {
        // UID changed (login, logout, user switch) — reset session state
        if (state.cluSessionKey) {
          // Deactivate old key server-side (fire-and-forget)
          const _oldKey = state.cluSessionKey;
          const _apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
          fetch(`${_apiUrl}/api/v1/session/current`, {
            method: 'DELETE',
            headers: { 'X-API-Key': _oldKey },
          }).catch(() => {});
        }
        state.cluSessionKey = null;
        state.cluSessionKeyExpiresAt = 0;
        state.currentFirebaseUid = newUid;

        // Restore from localStorage if same-UID context (e.g. new tab) and key not expired.
        // Safe because: (1) key is UID-namespaced — cross-user reuse is impossible,
        // (2) recoverSession() dedup handles the one case where a timestamp-valid key is
        //     server-side inactive, recovering cleanly with a single exchange.
        if (newUid) {
          try {
            const storedKey = localStorage.getItem(_sessionKey(newUid));
            const storedExp = Number(localStorage.getItem(_sessionExpiry(newUid)));
            if (storedKey && storedExp > now()) {
              state.cluSessionKey = storedKey;
              state.cluSessionKeyExpiresAt = storedExp;
            }
          } catch {}
        }
      } else {
        state.currentFirebaseUid = newUid;
      }

      state.currentUserData = {
        firebaseUser: userData.firebaseUser ?? state.currentUserData?.firebaseUser ?? null as any,
        userInfo: userData.userInfo ?? state.currentUserData?.userInfo ?? null as any,
        role,
        organizationId,
        email,
        fetchedAt: now(),
        expiresAt: now() + USER_DATA_TTL_MS,
      };
      
      console.log('[UserSessionRegistry.syncFromAuthContext] ✅ Datos actualizados en state:', {
        email: state.currentUserData.email || '[MISSING]',
        role: state.currentUserData.role || '[MISSING]',
        organizationId: state.currentUserData.organizationId || '[MISSING]',
      });
      
      // Invalidate headers cache since user data changed
      state.authHeadersCache.clear();
      notify();

      // Trigger CLU session key exchange if we have a Firebase user and no valid key.
      // Guard: skip if an exchange completed within the last 30s — prevents cascades where
      // rapid syncFromAuthContext re-renders create a new key that deactivates the current one.
      // Claim/join goes through the single shared gate (_claimOrJoinSessionExchange) —
      // fire-and-forget either way, same as before this was consolidated.
      const MIN_EXCHANGE_INTERVAL_MS = 30_000;
      const exchangeIsStale = (now() - state.lastExchangeCompletedAt) > MIN_EXCHANGE_INTERVAL_MS;
      if (userData.firebaseUser && (!state.cluSessionKey || state.cluSessionKeyExpiresAt <= now()) && exchangeIsStale) {
        _claimOrJoinSessionExchange(async () => {
          const { getAuth } = await import('firebase/auth');
          const fbUser = getAuth().currentUser;
          return fbUser ? fbUser.getIdToken(false) : null;
        });
      }
    } else {
      console.warn('[UserSessionRegistry.syncFromAuthContext] ⚠️ No se actualizó porque todos los campos son undefined');
    }
  },

  /**
   * Adopt a CLU session key issued inline by POST /api/auth/gateway.
   * This is the primary path for web logins: the backend issues the key in the
   * same request that verified the Firebase token, so no parallel
   * /api/v1/session/exchange call (and no second token verification) is needed.
   * _doSessionExchange remains as fallback for recovery and non-gateway flows.
   */
  adoptSessionKey(keyValue: string, expiresAtIso: string, firebaseUid: string) {
    if (!keyValue || !expiresAtIso || !firebaseUid) return;
    const expiresAt = new Date(expiresAtIso).getTime();
    if (!Number.isFinite(expiresAt) || expiresAt <= now()) return;

    // Adoption can run BEFORE syncFromAuthContext sets currentFirebaseUid.
    // Handle the UID transition here so the later sync sees a matching UID
    // and does NOT reset/deactivate this fresh key. On a user switch,
    // deactivate the previous user's key server-side (same as sync does).
    if (firebaseUid !== state.currentFirebaseUid) {
      if (state.cluSessionKey) {
        const _oldKey = state.cluSessionKey;
        const _apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
        fetch(`${_apiUrl}/api/v1/session/current`, {
          method: 'DELETE',
          headers: { 'X-API-Key': _oldKey },
        }).catch(() => {});
      }
      state.currentFirebaseUid = firebaseUid;
    }

    state.cluSessionKey = keyValue;
    state.cluSessionKeyExpiresAt = expiresAt;
    // Stamp the cooldown so syncFromAuthContext re-renders right after login
    // don't fire a redundant exchange that would deactivate this fresh key.
    state.lastExchangeCompletedAt = now();
    try {
      localStorage.setItem(_sessionKey(firebaseUid), keyValue);
      localStorage.setItem(_sessionExpiry(firebaseUid), String(expiresAt));
    } catch {}
    try { localStorage.removeItem('clu_session_key'); localStorage.removeItem('clu_session_key_expires_at'); } catch {}
    state.authHeadersCache.clear();
    console.log('[UserSessionRegistry.adoptSessionKey] ✅ CLU session key adopted from auth gateway');
    notify();
  },

  /**
   * Invalidate user data cache.
   * Does NOT touch the session key — it has an independent 24h server-side lifecycle.
   * Clearing the session key here would trigger an implicit _doSessionExchange on the
   * next request, which calls deactivate_old_session_keys and kills in-flight requests.
   */
  invalidateUserData() {
    state.currentUserData = null;
    state.authHeadersCache.clear();
    // Stamp lastExchangeCompletedAt BEFORE notify() so that any syncFromAuthContext
    // triggered by the React re-render sees a recent timestamp and the 30s guard
    // blocks it from racing with the recovery's own forceSyncSession call.
    state.lastExchangeCompletedAt = now();
    notify();
  },

  /**
   * Take the key another tab of this same browser already obtained, when the
   * one in memory is stale. Every exchange writes its key to localStorage, so
   * the stored key is the newest this browser has; the `storage` listener
   * normally hands it over, but a tab Safari kept suspended misses the event
   * and wakes up holding a key the server already rotated (2026-10-21,
   * production: ef5e1263 "has expired" on four calls at once). Exchanging
   * again from that tab would rotate the key every OTHER tab is using — the
   * ping-pong that showed up as 7 exchanges in 4 minutes.
   */
  adoptNewerStoredKey(): boolean {
    const uid = state.currentFirebaseUid;
    if (!uid) return false;
    try {
      const storedKey = localStorage.getItem(_sessionKey(uid));
      const storedExp = Number(localStorage.getItem(_sessionExpiry(uid)));
      if (!storedKey || storedKey === state.cluSessionKey || !(storedExp > now())) return false;
      state.cluSessionKey = storedKey;
      state.cluSessionKeyExpiresAt = storedExp;
    } catch {
      return false;
    }
    state.authHeadersCache.clear();
    console.log('[UserSessionRegistry.adoptNewerStoredKey] ✅ CLU session key adopted from another tab');
    notify();
    return true;
  },

  /**
   * Recover session after a 403 inactive/expired error.
   * Unlike invalidateUserData(), this explicitly issues a new session key
   * instead of relying on the 30s staleness timer in syncFromAuthContext —
   * unless another tab already has a newer one (see adoptNewerStoredKey).
   * `adoptStored: false` forces the exchange, for a caller whose retry with
   * the adopted key was refused too.
   *
   * Only the KEY is replaced. The user (currentUserData) is not touched: a
   * rotated key does not change who is signed in, and clearing it made the
   * whole app see "no user" for the length of the exchange — a refetch of
   * /api/auth/status and the jump back to the home screen that read as a
   * logout.
   */
  async recoverSession(options: { adoptStored?: boolean } = {}): Promise<boolean> {
    const { adoptStored = true } = options;
    // If a recovery exchange is already in-flight, join it instead of starting a parallel one.
    // Parallel exchanges are the root cause of the cascade: each one calls
    // deactivate_old_session_keys which kills the other's freshly-created key.
    // Joining does NOT run the destructive cleanup below — that only applies
    // when THIS call is the one starting a fresh recovery.
    if (state.sessionExchangeInFlight) {
      try {
        await state.sessionExchangeInFlight;
        return !!(state.cluSessionKey && state.cluSessionKeyExpiresAt > now());
      } catch {
        return false;
      }
    }

    if (adoptStored && this.adoptNewerStoredKey()) {
      return true;
    }

    // Not in flight — we're starting fresh recovery. This cleanup and the
    // _claimOrJoinSessionExchange() claim right after it are both
    // synchronous (no await in between), so nothing else can observe
    // (key=null, inflight=null) in the gap and start a parallel exchange of
    // its own — same ordering guarantee as before, now split across two
    // statements instead of one inline placeholder, not weakened by it.
    state.authHeadersCache.clear();
    state.cluSessionKey = null;
    state.cluSessionKeyExpiresAt = 0;
    state.lastExchangeCompletedAt = now();
    try {
      if (state.currentFirebaseUid) {
        localStorage.removeItem(_sessionKey(state.currentFirebaseUid));
        localStorage.removeItem(_sessionExpiry(state.currentFirebaseUid));
      }
      localStorage.removeItem('clu_session_key');
      localStorage.removeItem('clu_session_key_expires_at');
    } catch {}

    const { promise } = _claimOrJoinSessionExchange(async () => {
      let fbUser: any = firebaseAuth.currentUser;
      if (!fbUser) {
        const { onAuthStateChanged } = await import('firebase/auth');
        fbUser = await new Promise<any>((resolve) => {
          const timer = setTimeout(() => { unsubscribe(); resolve(null); }, 4000);
          const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
            clearTimeout(timer);
            unsubscribe();
            resolve(user);
          });
        });
      }
      if (!fbUser) return null;
      return fbUser.getIdToken(true);
    });

    try {
      await promise;
    } catch {
      // Best-effort join — the real outcome is the key-state check below,
      // not whether the exchange promise itself resolved or rejected.
    }
    notify();
    return !!(state.cluSessionKey && state.cluSessionKeyExpiresAt > now());
  },

  /**
   * Clear user data and header cache.
   * Does NOT touch the session key — it has an independent 24h server-side lifecycle.
   * Does NOT null sessionExchangeInFlight — if an exchange is in-flight, let it complete
   * to avoid a second parallel exchange that would kill the first one's key.
   * Use recoverSession() explicitly when a new session key is needed.
   */
  /**
   * Whether this browser holds an unexpired CLU session key for this Firebase
   * UID. The backend authenticates on that key alone (X-API-Key), so a Firebase
   * ID-token refresh that fails — Safari answered securetoken.googleapis.com
   * with a preflight 403 on 2026-10-21 — is not a lost session while it lives.
   */
  hasValidStoredSessionKey(uid: string | null | undefined): boolean {
    if (!uid) return false;
    try {
      const key = localStorage.getItem(_sessionKey(uid));
      const exp = Number(localStorage.getItem(_sessionExpiry(uid)));
      return !!key && exp > now();
    } catch {
      return false;
    }
  },

  clearCache() {
    state.currentUserData = null;
    state.authHeadersCache.clear();
    state.inFlight.userData = null;
    state.inFlight.headers = null;
    notify();
  },
  
  /**
   * Logout: deactivate session key server-side and clear all local state.
   * Call this on intentional logout (manual or auto). Do NOT use clearCache() for logout.
   */
  async logout(uid?: string): Promise<void> {
    const uidToUse = uid || state.currentFirebaseUid;

    // Deactivate server-side (fire-and-forget — don't block logout on network)
    if (state.cluSessionKey) {
      const _keyToKill = state.cluSessionKey;
      const _apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      fetch(`${_apiUrl}/api/v1/session/current`, {
        method: 'DELETE',
        headers: { 'X-API-Key': _keyToKill },
      }).catch(() => {});
    }

    // Clear in-memory state
    state.cluSessionKey = null;
    state.cluSessionKeyExpiresAt = 0;
    state.currentFirebaseUid = null;
    state.currentUserData = null;
    state.authHeadersCache.clear();
    state.inFlight.userData = null;
    state.inFlight.headers = null;
    state.sessionExchangeInFlight = null;
    state.lastExchangeCompletedAt = now();

    // Clear UID-namespaced localStorage
    if (uidToUse) {
      try {
        localStorage.removeItem(_sessionKey(uidToUse));
        localStorage.removeItem(_sessionExpiry(uidToUse));
      } catch {}
    }
    // Clean up legacy global key
    try {
      localStorage.removeItem('clu_session_key');
      localStorage.removeItem('clu_session_key_expires_at');
    } catch {}

    notify();
  },

  /**
   * Get current state (for debugging)
   */
  getState() {
    return {
      userData: state.currentUserData,
      loading: state.loading,
      error: state.error,
      isUserDataFresh: isUserDataFresh(),
      isHeadersFresh: state.authHeadersCache.size > 0,
    };
  },
};

/**
 * React hook to use current user (backward compatible with AuthContext)
 */
export function useCurrentUser() {
  const [, setTick] = useState(0);
  
  useEffect(() => {
    const update = () => setTick(t => t + 1);
    state.subscribers.add(update);
    
    // Load user data on mount if not cached
    if (!isUserDataFresh() && !state.inFlight.userData) {
      UserSessionRegistry.getCurrentUser().catch(() => {});
    }
    
    return () => {
      state.subscribers.delete(update);
    };
  }, []);
  
  return {
    currentUser: state.currentUserData?.firebaseUser || null,
    userInfo: state.currentUserData?.userInfo || null,
    email: state.currentUserData?.email || null,
    role: state.currentUserData?.role || null,
    organizationId: state.currentUserData?.organizationId || null,
    loading: state.loading.userData,
    error: state.error.userData,
    refresh: () => UserSessionRegistry.getCurrentUser(true),
  };
}

/**
 * React hook to use auth headers
 */
export function useAuthHeaders(options: { orgId?: number; includeToken?: boolean } = {}) {
  const [, setTick] = useState(0);
  const cacheKey = `${options.orgId || 'default'}|${options.includeToken ?? true}`;
  
  useEffect(() => {
    const update = () => setTick(t => t + 1);
    state.subscribers.add(update);
    
    // Load headers on mount if not cached
    if (!isHeadersFresh() && !state.inFlight.headers) {
      UserSessionRegistry.getAuthHeaders(options).catch(() => {});
    }
    
    return () => {
      state.subscribers.delete(update);
    };
  }, [JSON.stringify(options)]);
  
  return {
    headers: state.authHeadersCache.get(cacheKey)?.headers || {},
    loading: state.loading.headers,
    error: state.error.headers,
    refresh: () => UserSessionRegistry.getAuthHeaders(options),
  };
}

